import { useDialog } from 'muibox';
import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';

export interface OnlyPromoteLink {
    id: number;
    onlyfansId: number;
    name: string;
    platform: string;
    url: string;
    sortOrder: number;
    clickCount: number;
    active: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface CreateOnlyPromoteLinkPayload {
    name: string;
    platform: string;
    url: string;
    sortOrder?: number;
}

export interface UpdateOnlyPromoteLinkPayload {
    name?: string;
    platform?: string;
    url?: string;
    sortOrder?: number;
    active?: boolean;
}

const QUERY_KEY = 'onlyPromoteLinks';

const useOnlyPromoteLinks = () => {
    const settingsContext = useContext(SettingsContext);
    const userContext = useContext(UserContext);
    const params = useParams();
    const axios = useAxios();
    const dialog = useDialog();
    const queryClient = useQueryClient();

    const baseUrl = `${settingsContext.routes.menu.base}${params.userId}/links`;

    const getLinks = async (): Promise<OnlyPromoteLink[]> => {
        return axios
            .get(baseUrl, { params: { includeInactive: 'true' } })
            .then(response => response.data)
            .catch(error => {
                handleHttpError(error, dialog);
                return [];
            });
    };

    const linksQuery = useQuery([QUERY_KEY, params.userId], getLinks, {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!(userContext.jwtToken && params.userId && settingsContext.apiKey),
    });

    const createMutation = useMutation(
        async (payload: CreateOnlyPromoteLinkPayload) => {
            const response = await axios.post(baseUrl, payload);
            return response.data as OnlyPromoteLink;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEY, params.userId]);
            },
            onError: error => {
                handleHttpError(error, dialog);
            },
        },
    );

    const updateMutation = useMutation(
        async ({ linkId, payload }: { linkId: number; payload: UpdateOnlyPromoteLinkPayload }) => {
            const response = await axios.put(`${baseUrl}/${linkId}`, payload);
            return response.data as OnlyPromoteLink;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEY, params.userId]);
            },
            onError: error => {
                handleHttpError(error, dialog);
            },
        },
    );

    const deleteMutation = useMutation(
        async (linkId: number) => {
            await axios.delete(`${baseUrl}/${linkId}`);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEY, params.userId]);
            },
            onError: error => {
                handleHttpError(error, dialog);
            },
        },
    );

    return {
        linksQuery,
        createLink: createMutation.mutateAsync,
        isCreateLinkLoading: createMutation.isLoading,
        updateLink: updateMutation.mutateAsync,
        isUpdateLinkLoading: updateMutation.isLoading,
        deleteLink: deleteMutation.mutateAsync,
        isDeleteLinkLoading: deleteMutation.isLoading,
    };
};

export default useOnlyPromoteLinks;
